import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  PauseIcon, 
  StopIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/solid';
import { useAudio } from '../contexts/AudioContext';

const FloatingPlayer: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentSession,
    currentShow,
    currentEpisode,
    currentChannel,
    currentSegments,
    currentPanelists,
    isPlaying,
    isMuted,
    currentSegmentIndex,
    currentSpeaker,
    play,
    pause,
    stop,
    toggleMute,
    nextSegment,
    previousSegment,
    setCurrentSegment,
    clearSession,
    totalDuration
  } = useAudio();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Don't render if no active session
  if (!currentSession || !currentShow) {
    return null;
  }

  const currentSegment = currentSegments[currentSegmentIndex];
  const currentPanelist = currentPanelists.find(p => p._id === currentSpeaker);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleGoToSession = () => {
    navigate(`/session/${currentSession._id}`);
  };

  const handleClose = () => {
    clearSession();
  };

  // Mobile view - minimal controls
  if (isMobile) {
    return (
      <div className="floating-player-mobile animate-slide-up">
        <div className="flex items-center justify-between p-3">
          {/* Left side - Show info */}
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            {currentChannel?.logoUrl ? (
              <img 
                src={currentChannel.logoUrl} 
                alt={currentChannel.name}
                className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">
                  {currentChannel?.name?.charAt(0) || 'N'}
                </span>
              </div>
            )}
            
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {currentEpisode?.title || currentShow?.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {currentChannel?.name} • {currentShow?.name}
              </p>
            </div>
          </div>

          {/* Right side - Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={isPlaying ? pause : play}
              className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-200"
            >
              {isPlaying ? (
                <PauseIcon className="w-5 h-5" />
              ) : (
                <PlayIcon className="w-5 h-5" />
              )}
            </button>
            
            <button
              onClick={handleClose}
              className="w-8 h-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop view - expandable controls
  return (
    <div className={`floating-player animate-slide-up ${
      isMinimized ? 'w-16 h-16' : isExpanded ? 'w-96' : 'w-80'
    }`}>
      {isMinimized ? (
        // Minimized state - just a play button
        <div className="w-full h-full flex items-center justify-center">
          <button
            onClick={() => setIsMinimized(false)}
            className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-200"
          >
            {isPlaying ? (
              <PauseIcon className="w-6 h-6" />
            ) : (
              <PlayIcon className="w-6 h-6" />
            )}
          </button>
        </div>
      ) : (
        // Normal/expanded state
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Now Playing
              </h3>
              <button
                onClick={() => setIsMinimized(true)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <ChevronDownIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {isExpanded ? (
                  <ChevronUpIcon className="w-4 h-4" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Show info */}
          <div className="flex items-center space-x-3 mb-3">
            {currentChannel?.logoUrl ? (
              <img 
                src={currentChannel.logoUrl} 
                alt={currentChannel.name}
                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg font-bold">
                  {currentChannel?.name?.charAt(0) || 'N'}
                </span>
              </div>
            )}
            
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {currentEpisode?.title || currentShow?.name}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {currentChannel?.name} • {currentShow?.name}
              </p>
              {currentSegment && (
                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                  Segment {currentSegmentIndex + 1} of {currentSegments.length}
                </p>
              )}
            </div>
          </div>

          {/* Current speaker */}
          {currentPanelist && (
            <div className="flex items-center space-x-2 mb-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {currentPanelist.name.charAt(0)}
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {currentPanelist.name} is speaking
              </span>
            </div>
          )}

          {/* Main controls */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={previousSegment}
              disabled={currentSegmentIndex === 0}
              className="w-8 h-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>

            <button
              onClick={isPlaying ? pause : play}
              className="w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-200"
            >
              {isPlaying ? (
                <PauseIcon className="w-6 h-6" />
              ) : (
                <PlayIcon className="w-6 h-6" />
              )}
            </button>

            <button
              onClick={nextSegment}
              disabled={currentSegmentIndex === currentSegments.length - 1}
              className="w-8 h-8 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Secondary controls */}
          <div className="flex items-center justify-between">
            <button
              onClick={toggleMute}
              className="flex items-center space-x-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              {isMuted ? (
                <SpeakerXMarkIcon className="w-4 h-4" />
              ) : (
                <SpeakerWaveIcon className="w-4 h-4" />
              )}
              <span className="text-xs">{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>

            <button
              onClick={stop}
              className="flex items-center space-x-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <StopIcon className="w-4 h-4" />
              <span className="text-xs">Stop</span>
            </button>

            <button
              onClick={handleGoToSession}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
            >
              View Full Player
            </button>
          </div>

          {/* Expanded content */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              {/* Playlist */}
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {currentSegments.map((segment, index) => (
                  <button
                    key={segment._id}
                    onClick={() => setCurrentSegment(index)}
                    className={`w-full text-left p-2 rounded-lg transition-colors duration-200 ${
                      index === currentSegmentIndex
                        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="text-xs font-medium truncate">{segment.title}</div>
                    <div className="text-xs opacity-75">
                      {segment.audioDuration ? formatTime(segment.audioDuration) : 'No duration'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FloatingPlayer;
